import {AjaxFactory, HttpMethod} from '../../_libs/redux-observable-list';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {ReactReduxModal} from '../../core-utilities/react-redux-modals';
import {getSuccessOnlyResponseStatus} from '../../core-utilities/redux-request/utils';
import {ReactReduxModalHOC} from '../../core-utilities/react-redux-modals/components/ReactReduxModalHOC';
import {makeActionCreators} from '../../core-utilities/redux';
import {ReduxRequest} from '../../core-utilities/redux-request';
import {ChatChannelModal, modalOptions} from '../components/ChatChannelModal';
import {buildModalSupportEpics} from '../epics/modalSupportEpics';
import {getChatModal, getChatModalSubscriptionRequest} from '../reducers/state';
import {CHAT_MODAL_LIST_PREFIX} from './state';
import {ViewMode} from '../../core-utilities/enums/ViewMode';
import {logger} from './logger';

// Usage note: the showModal payload requires the parentId to display

const UPDATE_SUBSCRIPTION_STATE = 'updateSubscription';

const options = {
  prefix: CHAT_MODAL_LIST_PREFIX,
  getState: getChatModal,
  modalOptions,
  initialState: {
    parentId: null,
    channel: null,
    err: null,
    permissionsErr: null,
    permissionsMode: ViewMode.LOADING,
    allowVideoChat: null,
    [UPDATE_SUBSCRIPTION_STATE]: ReduxRequest.initialState,
  },
  logger,
};

export const reactReduxModal = new ReactReduxModal(options);

function buildUpdateSubscriptionAjaxFactory(options) {
  return new AjaxFactory({
    crossDomain: options.crossDomain,
    method: request => request.subscribe ? HttpMethod.PUT : HttpMethod.DELETE,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.parentId}/subscription`,
    getToken: options.getToken,
    responseType: request => 'json',
  });
}


// Start - Chat Channel Requests:
const reduxRequestOptions = {
  prefix: `${options.prefix}/${UPDATE_SUBSCRIPTION_STATE}`,
  getState: getChatModalSubscriptionRequest,
  requestFactory: buildUpdateSubscriptionAjaxFactory({
    ...BASE_REDUX_LIST_OPTIONS,
  }),
  buildRequest: (state, payload) => payload,
  getResponseStatus: getSuccessOnlyResponseStatus,
  logger: options.logger,
};
const updateSubscriptionRequest = new ReduxRequest(reduxRequestOptions);
// End - Chat Channel Requests:

/** @type {ChatModalActions} */
reactReduxModal.actions = buildActions(reactReduxModal.actions, updateSubscriptionRequest.actions);
reactReduxModal.reducers.reducer = buildReducers(reactReduxModal, updateSubscriptionRequest);

const modalConfig = {
  actions: reactReduxModal.actions,
  getState: getChatModal,
  logger,
};

const supportEpics = buildModalSupportEpics(modalConfig);

reactReduxModal.epics.addEpics(supportEpics);
reactReduxModal.epics.addEpics(updateSubscriptionRequest.epics.epics);

options.modalComponent = ReactReduxModalHOC(ChatChannelModal, modalConfig);

// Exports
/** @type {ChatModalActions} */
export const actions = reactReduxModal.actions;

function buildActions(actions, updateSubscriptionActions) {
  return {
    ...makeActionCreators(CHAT_MODAL_LIST_PREFIX, [{
      name: 'getChannelSuccess',
      type: 'GET_CHANNEL_SUCCESS',
    }, {
      name: 'getChannelError',
      type: 'GET_CHANNEL_ERROR',
    }, {
      name: 'linkChannelSuccess',
      type: 'LINK_CHANNEL_SUCCESS',
    }, {
      name: 'linkChannelError',
      type: 'LINK_CHANNEL_ERROR',
    }, {
      name: 'getAppPermissionsSuccess',
      type: 'GET_APP_PERMISSIONS_SUCCESS',
    }, {
      name: 'getAppPermissionsError',
      type: 'GET_APP_PERMISSIONS_ERROR',
    }], actions),
    [UPDATE_SUBSCRIPTION_STATE]: updateSubscriptionActions,
  };
}

function buildReducers(reactReduxModal, updateSubscriptionRequest) {
  const updateSubscriptionReducer = updateSubscriptionRequest.reducers.buildSubStateReducer(UPDATE_SUBSCRIPTION_STATE);
  /** @type {ChatModalActions} */
  const actions = reactReduxModal.actions;
  const baseReducer = reactReduxModal.reducers.reducer;

  return function (state = reactReduxModal.reducers.initialState, action) {
    switch (action.type) {
      case actions.getChannelSuccess.TYPE:
      case actions.linkChannelSuccess.TYPE:
        return channelSuccessReducer(state, action);

      case actions.getChannelError.TYPE:
      case actions.linkChannelError.TYPE:
        return channelErrorReducer(state, action);

      case actions.getAppPermissionsSuccess.TYPE:
        return getAppPermissionsSuccessReducer(state, action);

      case actions.getAppPermissionsError.TYPE:
        return getAppPermissionsErrorReducer(state, action);

      default:
        // chain remaining reducers
        const updateSubscriptionState = updateSubscriptionReducer(state, action);
        return baseReducer(updateSubscriptionState, action);
    }
  };

  /**
   * @param {ReactReduxModalState} state
   * @param {ReduxAction<ChatModalGetChannelSuccessPayload>} action
   * @return {ReactReduxModalState}
   */
  function channelSuccessReducer(state, action) {
    return {
      ...state,
      err: null,
      parentId: action.payload.channel.id,
      channel: action.payload.channel,
    };
  }

  /**
   * @param {ReactReduxModalState} state
   * @param {ReduxAction<ChatModalErrorPayload>} action
   * @return {ReactReduxModalState}
   */
  function channelErrorReducer(state, action) {
    return {
      ...state,
      err: action.payload.err,
    };
  }

  /**
   * @param {ReactReduxModalState} state
   * @param {ReduxAction<ChatModalGetAppPermissionsSuccessPayload>} action
   * @return {ReactReduxModalState}
   */
  function getAppPermissionsSuccessReducer(state, action) {
    const permissions = action.payload.permissions || {};
    return {
      ...state,
      permissionsErr: null,
      permissionsMode: ViewMode.SUCCESS,
      allowVideoChat: !!permissions.videoChat,
    };
  }

  /**
   * @param {ReactReduxModalState} state
   * @param {ReduxAction<ChatModalErrorPayload>} action
   * @return {ReactReduxModalState}
   */
  function getAppPermissionsErrorReducer(state, action) {
    return {
      ...state,
      permissionsErr: action.payload.err,
      permissionsMode: ViewMode.EOTHER,
      allowVideoChat: null,
    };
  }
}

/**
 * @typedef {ReactReduxModalActions} ChatModalActions
 * @property {Function|ReduxActionCreator<ChatModalGetChannelSuccessPayload>} getChannelSuccess
 * @property {Function|ReduxActionCreator<ChatModalErrorPayload>} getChannelError
 * @property {Function|ReduxActionCreator<ChatModalGetChannelSuccessPayload>} linkChannelSuccess
 * @property {Function|ReduxActionCreator<ChatModalErrorPayload>} linkChannelError
 * @property {Function|ReduxActionCreator<ChatModalGetAppPermissionsSuccessPayload>} getAppPermissionsSuccess
 * @property {Function|ReduxActionCreator<ChatModalErrorPayload>} getAppPermissionsError
 * @property {ReduxRequestActions} updateSubscription
 */

/**
 * @typedef {Object} ChatModalGetChannelSuccessPayload
 * @property {Object} channel
 */

/**
 * @typedef {Object} ChatModalErrorPayload
 * @property {Error} err
 */

/**
 * @typedef {Object} ChatModalGetAppPermissionsSuccessPayload
 * @property {Object} permissions
 */



// WEBPACK FOOTER //
// ./src/js/comments/redux/chatChannelModal.js